/**
 * PromptInput — AI Elements pattern
 * Auto-resizing textarea with submit button.
 */
import React, { useRef, useCallback, useEffect, type KeyboardEvent } from 'react';
import { cn } from '@/lib/utils';
import { CONTROLS } from '@/lib/control-instrumentation';
import { ArrowUp, Square } from 'lucide-react';

// Base height of a single-row textarea (approx 32px)
const BASE_HEIGHT = 32;
const MAX_HEIGHT = 90;

interface PromptInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  onCancel?: () => void;
  onExpandedChange?: (isExpanded: boolean) => void;
  focusSignal?: number;
  placeholder?: string;
  isLoading?: boolean;
  disabled?: boolean;
  compact?: boolean;
  minRows?: number;
  className?: string;
}

export const PromptInput: React.FC<PromptInputProps> = ({
  value,
  onChange,
  onSubmit,
  onCancel,
  onExpandedChange,
  focusSignal = 0,
  placeholder = 'Ask anything…',
  isLoading = false,
  disabled = false,
  compact = false,
  minRows = 1,
  className,
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const wasExpandedRef = useRef(false);
  const focusPulseTimeoutRef = useRef<number | null>(null);
  const [showFocusPulse, setShowFocusPulse] = React.useState(false);
  const resolvedMinRows = Math.max(1, Math.floor(minRows));
  const minTextareaHeight = BASE_HEIGHT + (resolvedMinRows - 1) * 22;

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        if (!isLoading && value.trim()) {
          onSubmit();
        }
      }
    },
    [isLoading, value, onSubmit],
  );

  const handleInput = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const newHeight = Math.min(Math.max(el.scrollHeight, minTextareaHeight), MAX_HEIGHT);
    el.style.height = `${newHeight}px`;
    
    // Track expansion state
    const isNowExpanded = newHeight > minTextareaHeight + 10; // 10px buffer
    if (isNowExpanded !== wasExpandedRef.current) {
      wasExpandedRef.current = isNowExpanded;
      onExpandedChange?.(isNowExpanded);
    }
  }, [minTextareaHeight, onExpandedChange]);

  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    if (!value) {
      el.style.height = `${minTextareaHeight}px`;
      if (wasExpandedRef.current) {
        wasExpandedRef.current = false;
        onExpandedChange?.(false);
      }
      return;
    }
    handleInput();
  }, [value, handleInput, minTextareaHeight, onExpandedChange]);

  useEffect(() => {
    if (!focusSignal) return;
    const el = textareaRef.current;
    if (!el) return;

    requestAnimationFrame(() => {
      handleInput();
      el.focus();
      const end = el.value.length;
      try {
        el.setSelectionRange(end, end);
      } catch {
        // Ignore browsers that do not support selection updates here.
      }
      setShowFocusPulse(true);
      if (focusPulseTimeoutRef.current !== null) {
        window.clearTimeout(focusPulseTimeoutRef.current);
      }
      focusPulseTimeoutRef.current = window.setTimeout(() => {
        setShowFocusPulse(false);
        focusPulseTimeoutRef.current = null;
      }, 1200);
    });
  }, [focusSignal, handleInput]);

  useEffect(() => {
    return () => {
      if (focusPulseTimeoutRef.current !== null) {
        window.clearTimeout(focusPulseTimeoutRef.current);
      }
    };
  }, []);

  const handleSubmitClick = () => {
    if (isLoading && onCancel) {
      onCancel();
    } else if (value.trim()) {
      onSubmit();
    }
  };

  // Check if scrolling is active
  const isScrollable = value.trim().length > 0 && textareaRef.current
    ? textareaRef.current.scrollHeight > MAX_HEIGHT
    : false;

  return (
    <div
      className={cn(
        'relative flex items-end gap-1.5 rounded-lg border border-border bg-background p-1.5 shadow-sm transition-all',
        'focus-within:border-primary/50 focus-within:ring-2 focus-within:ring-primary/35',
        compact && 'p-1',
        value.trim() && 'border-primary/30',
        showFocusPulse && 'ring-2 ring-primary/45 border-primary/45 animate-pulse',
        className,
      )}
    >
      <textarea
        ref={textareaRef}
        data-testid={CONTROLS.ai.promptInput.tid}
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          handleInput();
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        disabled={disabled}
        rows={resolvedMinRows}
        className={cn(
          'flex-1 resize-none bg-transparent px-2 py-1.5 text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none disabled:opacity-50',
          compact && 'py-1 leading-snug',
          'overflow-hidden',
          isScrollable && 'assistant-thin-scrollbar overflow-y-auto'
        )}
        style={{ maxHeight: MAX_HEIGHT, minHeight: minTextareaHeight }}
      />
      <button
        onClick={handleSubmitClick}
        data-testid={CONTROLS.ai.promptSend.tid}
        disabled={disabled || (!isLoading && !value.trim())}
        className={cn(
          'flex h-8 w-8 shrink-0 items-center justify-center rounded-md transition-all',
          compact && 'h-7 w-7',
          isLoading
            ? 'assistant-gradient hover:opacity-90 shadow-sm'
            : value.trim()
              ? 'assistant-gradient hover:opacity-90 shadow-sm'
              : 'bg-muted text-muted-foreground',
        )}
        aria-label={isLoading ? 'Stop' : 'Send'}
      >
        {isLoading ? <Square className="h-3.5 w-3.5" /> : <ArrowUp className="h-5 w-5" />}
      </button>
    </div>
  );
};
